import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Attach bearer token from localStorage as a fallback (cookies still preferred).
api.interceptors.request.use((cfg) => {
  const tok = localStorage.getItem("sharda_token");
  if (tok && !cfg.headers.Authorization) {
    cfg.headers.Authorization = `Bearer ${tok}`;
  }
  cfg.metadata = { startedAt: Date.now() };
  return cfg;
});

/* Dispatch 17 -- Canonical response-interceptor policy.
 *
 *   * 401 on non-auth endpoints  -> clear session token + redirect to
 *     /login. The redirect fires ONCE per 2-second window even if a
 *     burst of concurrent requests all fail (see `_isRedirecting`).
 *     No toast -- the login page handles the receiving-side UI.
 *   * 401 on /api/auth/* endpoints -> NEVER redirect. Reject as-is so
 *     the login / refresh / force-change form displays the server
 *     detail (e.g. wrong password).
 *   * 403 / 422 / other 4xx / 5xx / network -> auto-surface via
 *     `errorToast`. The mapper handles friendly copy + pydantic detail
 *     arrays + a 2-second dedup window so a page-load burst of
 *     identical 403s renders exactly one toast.
 *   * Every auto-surfaced error is stamped `err._surfaced = true`.
 *     Legacy `catch (e) { errorToast(e) }` at call sites still works
 *     because `errorToast` short-circuits on that flag.
 *   * Callers that want to swallow errors pass `{ silent: true }` on
 *     the axios config (background polls, best-effort fetches).
 *
 * Also feeds the errorToast ring buffer of the last N API calls for
 * the bug reporter modal (Phase 17.1 behaviour preserved).
 */

// Redirect race safeguard -- one `/login` redirect per 2s window.
let _isRedirecting = false;
let _isRedirectingResetTimer = null;

function _scheduleRedirectReset() {
  if (_isRedirectingResetTimer) clearTimeout(_isRedirectingResetTimer);
  _isRedirectingResetTimer = setTimeout(() => {
    _isRedirecting = false;
    _isRedirectingResetTimer = null;
  }, 2000);
}

// True for any /auth/* URL -- login, refresh, logout, force-change-password,
// reset-password/consume. These must NEVER trigger the auto-redirect.
function _isAuthUrl(url) {
  if (!url) return false;
  return /\/api\/auth\//.test(url) || /^\/?auth\//.test(url);
}

// Lazy-imported helpers -- errorToast imports `api`, so we break the
// require cycle by resolving them the first time a response comes in.
let _errorToastFn = null;
let _recordApiCall = null;
async function _lazyLoadErrorHelpers() {
  if (_errorToastFn && _recordApiCall) return;
  try {
    const mod = await import("./errorToast");
    _errorToastFn = mod.default || mod.errorToast;
    _recordApiCall = mod.recordApiCall || (() => {});
  } catch {
    _errorToastFn = () => {};
    _recordApiCall = () => {};
  }
}

api.interceptors.response.use(
  (res) => {
    _lazyLoadErrorHelpers().then(() => {
      if (_recordApiCall) {
        _recordApiCall({
          method: res.config?.method?.toUpperCase(),
          url: res.config?.url,
          status: res.status,
          ms: Date.now() - (res.config?.metadata?.startedAt || Date.now()),
        });
      }
    });
    return res;
  },
  async (err) => {
    const status = err?.response?.status || 0;
    const url = err?.config?.url || "";
    const config = err?.config || {};

    // D55-d Part 2 -- 403 with a `denied_permission_key` payload fires
    // a browser-level event so the shell can auto-open the Request
    // Access modal. Silent no-op if no listener is attached.
    if (status === 403 && typeof window !== "undefined") {
      const detail = err?.response?.data?.detail;
      const key = (detail && typeof detail === "object")
        ? detail.denied_permission_key : null;
      if (key) {
        try {
          window.dispatchEvent(new CustomEvent("rbac:access-denied", {
            detail: { permission_key: key, url },
          }));
        } catch (_e) { /* ignore */ }
      }
    }

    await _lazyLoadErrorHelpers();
    if (_recordApiCall) {
      _recordApiCall({
        method: config?.method?.toUpperCase(),
        url,
        status,
        ms: Date.now() - (config?.metadata?.startedAt || Date.now()),
        error: err?.message,
      });
    }

    const silent = config?.silent === true || config?._silent === true;

    // D48-c widget bugfix: aborted / canceled requests are cleanup
    // side-effects (React StrictMode double-effect, unmount during
    // fetch, user navigating away). They are NEVER worth toasting.
    // Suppress at the interceptor so every caller benefits without
    // opting in with `silent`.
    const isCanceled = (
      err?.code === "ERR_CANCELED"
      || err?.name === "CanceledError"
      || err?.message === "canceled"
      || (err?.config?.signal && err.config.signal.aborted)
    );
    if (isCanceled) {
      err._surfaced = true;
      return Promise.reject(err);
    }

    // 401 -- redirect to /login UNLESS we were hitting an /auth/* URL
    // (wrong password on login must show the toast on the login page,
    // not bounce to itself; refresh 401 must let ForceChange / hydrate
    // handlers decide).
    if (status === 401 && !_isAuthUrl(url)) {
      if (!_isRedirecting) {
        _isRedirecting = true;
        _scheduleRedirectReset();
        try { localStorage.removeItem("sharda_token"); } catch (_e) { /* ignore */ }
        if (typeof window !== "undefined") {
          const path = window.location?.pathname || "/";
          const search = window.location?.search || "";
          const nextParam =
            path && path !== "/login"
              ? `?next=${encodeURIComponent(path + search)}`
              : "";
          // Defer to next tick so any in-flight state updates settle.
          setTimeout(() => {
            try { window.location.assign(`/login${nextParam}`); } catch (_e) { /* ignore */ }
          }, 0);
        }
      }
      err._surfaced = true;
      return Promise.reject(err);
    }

    // 403 / 422 / other non-2xx / network: auto-surface via errorToast
    // (unless the caller opted for silent). errorToast itself dedupes
    // identical messages within a 2-second window.
    if (!silent && !err._surfaced && _errorToastFn) {
      try { _errorToastFn(err); } catch (_e) { /* toast optional */ }
    }

    return Promise.reject(err);
  },
);

export default api;
